// Campos de configuração iguais aos do protótipo: número com − e +, texto, chips e chave.
// Salvam na hora (sem botão "Salvar"); o "Salvo" aparece ao lado do título.
import { useEffect, useState } from 'react';
import { useToast } from '../app/toast.tsx';
import { money } from '../lib/format.ts';
import { Icon } from './Icon.tsx';

export type NumKind = 'int' | 'money' | 'bps';

const show = (v: number, kind: NumKind) => (kind === 'money' ? (v / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+,)/g, '.') : kind === 'bps' ? (v / 100).toFixed(2).replace('.', ',') : String(v));
const parse = (raw: string, kind: NumKind) => {
  if (kind === 'money') { const t = raw.replace(/[^\d,.]/g, ''); const n = parseFloat(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t); return Number.isNaN(n) ? NaN : Math.round(n * 100); }
  if (kind === 'bps') { const n = parseFloat(raw.replace(/[^\d,.]/g, '').replace(',', '.')); return Number.isNaN(n) ? NaN : Math.round(n * 100); }
  return parseInt(raw.replace(/\D/g, ''), 10);
};
const limLabel = (v: number, kind: NumKind) => (kind === 'money' ? money(v) : kind === 'bps' ? `${(v / 100).toFixed(2).replace('.', ',')}%` : String(v));

export function CfgNum({ label, unit, value, onChange, lim, kind = 'int', hint }: {
  label: string; unit?: string; value: number; onChange: (v: number) => void; lim: [number, number, number]; kind?: NumKind; hint?: React.ReactNode;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(show(value, kind));
  useEffect(() => setDraft(show(value, kind)), [value, kind]);
  const commit = (raw: string) => {
    const n = parse(raw, kind);
    if (Number.isNaN(n)) { setDraft(show(value, kind)); return toast('Digite um número.', 'warn'); }
    const v = Math.max(lim[0], Math.min(lim[1], n));
    if (v !== n) toast(`O limite é de ${limLabel(lim[0], kind)} a ${limLabel(lim[1], kind)}.`, 'warn');
    if (v !== value) onChange(v); else setDraft(show(value, kind));
  };
  return (
    <div className="gl-row nf-row"><span>{label}{hint ? <small>{hint}</small> : null}</span>
      <div className="nf">
        <button aria-label={`Diminuir ${label}`} disabled={value <= lim[0]} onClick={() => onChange(Math.max(lim[0], value - lim[2]))}>−</button>
        {kind === 'money' && <em>R$</em>}
        <input type="text" inputMode={kind === 'bps' ? 'decimal' : 'numeric'} value={draft} aria-label={label}
          onChange={(e) => setDraft(e.target.value)} onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
        {unit && <em>{unit}</em>}
        <button aria-label={`Aumentar ${label}`} disabled={value >= lim[1]} onClick={() => onChange(Math.min(lim[1], value + lim[2]))}>+</button>
      </div>
    </div>
  );
}

export function CfgText({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <label className="gl-row gl-field"><span>{label}</span>
      <input className="gl-in" type={type} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onBlur={() => { if (draft.trim() !== value) onChange(draft.trim()); }}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
    </label>
  );
}

export function CfgChips<T extends string | number | boolean>({ value, options, onChange }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return <div className="chipset soft">{options.map(([k, l]) => <button key={String(k)} className={value === k ? 'on' : ''} aria-pressed={value === k} onClick={() => onChange(k)}>{l}</button>)}</div>;
}

export function CfgToggle({ label, value, onChange, sub }: { label: string; value: boolean; onChange: (v: boolean) => void; sub?: React.ReactNode }) {
  return (
    <div className="gl-row gl-tog"><span>{label}{sub ? <small>{sub}</small> : null}</span><b />
      <button role="switch" aria-checked={value} aria-label={label} onClick={() => onChange(!value)}><Icon name={value ? 'toggleOn' : 'toggleOff'} /></button></div>
  );
}

export function Saved({ at }: { at: number }) {
  const [on, setOn] = useState(false);
  useEffect(() => { if (!at) return; setOn(true); const t = window.setTimeout(() => setOn(false), 1400); return () => window.clearTimeout(t); }, [at]);
  return <span className={'pf-saved' + (on ? ' on' : '')} aria-live="polite">{on ? 'Salvo' : ''}</span>;
}
